import { HighlineService } from '@cof/auto-refinance-node-highline';
import { trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { TagKey } from '@cof/tag-catalog-fs-esign-mfe';
import { DebugService } from './debug.service.js';
import { ApiService } from '../../core/services/api/api.service.js';
import { REFI_ESIGN_CONST } from '../../../constants/app.constants.js';
import { SessionService } from '../../core/services/session/session.service.js';
import { MockContractService } from './mock-contract.service.js';
import { getAdditionalInputs, TitleType } from '../../e-title/etitle.config.js';
import { stratum } from '../../../constants/stratum.js';

class _EsignService {
  constructor() {
    this.debug = new DebugService('EsignService');
    this._contractFile = [];
    this._contractFileWithShipping = [];
    this._contractFilesReady = false;
    this._contractResponse = null;
    this._contractDetailsOnlyResponse = null;
    this.encryptedFields = [getAdditionalInputs().DRIVERS_LICENSE_NUMBER.fieldName, getAdditionalInputs().CO_BORROWER_DRIVERS_LICENSE_NUMBER.fieldName];
  }

  get contractFile() {
    return this._contractFile;
  }

  get contractFileWithShipping() {
    return this._contractFileWithShipping;
  }

  get contractFilesReady() {
    return this._contractFilesReady;
  }

  /* isMockNeeded() {
      let isMockNeeded = false;
      if (document?.location && !REFI_ESIGN_CONST.isProdEnv) {
        const testingParam = new URL(document.location)?.searchParams?.get('useMock');
        isMockNeeded = testingParam === 'true' || testingParam === 'yes';
      }
      return isMockNeeded;
    } */

  async fetchContract(shouldShowSignature, detailsOnly) {
    this.debug.log(`fetchContract called with shouldShowSignature ${shouldShowSignature} and detailsOnly ${detailsOnly}`);

    if (detailsOnly) {
      return this.fetchContractDetailsOnly();
    }

    if (this._contractResponse) {
      this.debug.log('found contract response in cache');
      return this._contractResponse;
    }
    try {
      const eAppId = SessionService.session?.eAppId;
      /* if (this.isMockNeeded()) {
              this.debug.log(`Fetching Mock contract...`);
              res = await ApiService.get(`${REFI_ESIGN_CONST.ESIGN_BFF_URL}/undefined/document?UseMimeo=true`);
            } else { */
      this.debug.log(`Fetching contract...`);
      const res = await ApiService.get(`${REFI_ESIGN_CONST.ESIGN_BFF_URL}/${eAppId}/document?shouldShowSignature=${shouldShowSignature}`);
      /* } */

      this.setContract(res.contractByteArray, 'contractNoShipping');
      this.setContract(res.shippingLabelByteArray, 'contractShipping');
      this._contractFilesReady = true;
      dispatchEvent(REFI_ESIGN_CONST.CONTRACT_FILES_READY_EVENT);

      this._contractResponse = res;
      return res;
    } catch (err) {
      this.debug.error('error in fetchContract', err);
      throw err;
    }
  }

  async fetchContractDetailsOnly() {
    if (this._contractDetailsOnlyResponse) {
      this.debug.log('found contract details only response in cache');
      return this._contractDetailsOnlyResponse;
    }
    try {
      const eAppId = SessionService.session?.eAppId;

      this.debug.log(`Fetching contract details only...`);
      const res = await ApiService.get(`${REFI_ESIGN_CONST.ESIGN_BFF_URL}/${eAppId}/document?detailsOnly=true`);

      this._contractDetailsOnlyResponse = res;
      return res;
    } catch (err) {
      this.debug.error('error in fetchDetailsOnly', err);
      throw err;
    }
  }

  async fetchMockContract() {
    const res = await MockContractService.fetchMockContractDetails();
    this.setContract(res.contractByteArray, 'contractNoShipping');
    this.setContract(res.shippingLabelByteArray, 'contractShipping');
    return res;
  }

  async eSignContract() {
    this.debug.log('eSignContract called');
    const eAppId = SessionService.session?.eAppId;
    try {
      const response = await ApiService.post(`${REFI_ESIGN_CONST.ESIGN_BFF_URL}/${eAppId}/esign-document`, { documentType: 'Contract' });
      this.debug.log('success in eSignContract', response);
      this.createCustomerProfile().then(res => {
        if (res.status === 500) {
          setTimeout(async () => {
            this.createCustomerProfile().then(resp => {
              if (resp.status === 500) {
                this.debug.log('Create Customer profile doc call failed after retry.');
              } else {
                this.sendCustomerProfileDocAnalytics();
                this.debug.log('Create Customer profile doc call is done after retry.');
              }
            });
          }, 1000);
        } else {
          this.sendCustomerProfileDocAnalytics();
          this.debug.log('Create Customer profile doc call is done.');
        }
      });
      return response;
    } catch (err) {
      this.debug.error('error in eSignContract', err);
      throw err;
    }
  }

  async createCustomerProfile() {
    this.debug.log('createCustomerProfile called');
    const eAppId = SessionService.session?.eAppId;
    try {
      const res = await ApiService.post(`${REFI_ESIGN_CONST.ESIGN_BFF_URL}/${eAppId}/esign-document`, { documentType: 'CustomerProfile' });
      this.debug.log('success in createCustomerProfile', res);
      return res;
    } catch (err) {
      this.debug.error('error in createCustomerProfile', err);
      const res = { status: 500 };
      return res;
    }
  }

  sendCustomerProfileDocAnalytics() {
    stratum.publishTag(TagKey.CUSTOMER_PROFILE_CUSTOMER_PROFILE_DOC_GENERATED);
    trackAnalyticsEvent('customer profile doc generated');
  }

  get contractResponse() {
    return this._contractResponse;
  }

  get contractDetailsOnlyResponse() {
    return this._contractDetailsOnlyResponse;
  }

  setContract(pdfArray, contractType) {
    const UTF8_STR = new Uint8Array(pdfArray);
    const BINARY_ARR = UTF8_STR.buffer;
    const contractBlob = new Blob([BINARY_ARR], { type: 'application/pdf' });
    if (contractType === 'contractNoShipping') {
      contractBlob.name = 'autorefi_contract.pdf';
      this._contractFile = contractBlob;
    } else {
      contractBlob.name = 'autorefi_contract_shipping_label.pdf';
      this._contractFileWithShipping = contractBlob;
    }
  }

  /**
   *
   * @param {{fieldType: string, fieldValue: string }[]} additionalFields
   * @param {string} documentType
   * @returns {Promise<void>}
   */
  async eSignTitle(additionalFields, documentType) {
    const eAppId = SessionService.session?.eAppId;
    const tagMap = {
      [TitleType.PoA]: {
        success: TagKey.POA_ESIGN_API_SUCCESS,
        failure: TagKey.POA_ESIGN_API_FAILED,
      },
      [TitleType.TitleAuth]: {
        success: TagKey.TITLE_AUTHORIZATION_ESIGN_API_SUCCESS,
        failure: TagKey.TITLE_AUTHORIZATION_ESIGN_API_FAILED,
      },
      [TitleType.TitleApp]: {
        success: TagKey.TITLE_APPLICATION_ESIGN_API_SUCCESS,
        failure: TagKey.TITLE_APPLICATION_ESIGN_API_FAILED,
      },
      [TitleType.SecurityAgreement]: {
        success: TagKey.SECURITY_AGREEMENT_ESIGN_API_SUCCESS,
        failure: TagKey.SECURITY_AGREEMENT_ESIGN_API_FAILED,
      },
    };

    try {
      if (additionalFields.length) {
        for (const field of additionalFields) {
          if (this.encryptedFields.includes(field.fieldType)) {
            field.fieldValue = await this.encryptValue(field.fieldValue);
          }
        }
      }
      const res = await ApiService.post(`${REFI_ESIGN_CONST.ESIGN_BFF_URL}/${eAppId}/esign-document`, { documentType: 'Title', additionalFields });
      this.debug.log('success in eSignTitle', res);
      stratum.publishTag(tagMap[documentType].success);
      return res;
    } catch (err) {
      this.debug.error('error in eSignTitle', err);
      stratum.publishTag(tagMap[documentType].failure);
      throw err;
    }
  }

  /**
   * Encrypts the provided value using public key
   * @async
   * @method encryptValue
   * @param {string} value
   * @returns {Promise<string>}
   */
  async encryptValue(value) {
    await HighlineService.getPublicKey(REFI_ESIGN_CONST.PUBLIC_KEY_PRODUCT_ID);
    let encryptedValue = '';
    try {
      encryptedValue = await HighlineService.encryptWithPublicKey(value);
    } catch (ex) {
      this.debug.error('Error occurred while trying to encrypt', ex);
    }
    return encryptedValue;
  }
}

export const EsignService = new _EsignService();
